/**
 * Coordinate-space changes for scene items (moving between groups, ungrouping,
 * fitting a group child). Everything goes through the same layout maths the
 * compositor and the editor use, so what the commands compute is what is drawn.
 *
 * An item's placement is described by one affine matrix (content pixels to
 * parent space). A change of space composes that matrix with the new space's
 * matrix and then writes it back as an `ItemTransform`:
 *   1. keep the item's own structure (alignment, bounds) when the result is
 *      still exact, verified against the target matrix;
 *   2. otherwise bake it: no bounds, top-left alignment, position, rotation and
 *      signed scale taken from the matrix.
 * Baking is exact for any rotation, flip, uniform or non-uniform scale of the
 * space as long as the item itself is not rotated off the space's axes while
 * the space scales non-uniformly: that would need a shear, which the transform
 * model cannot hold. The result then keeps the item's first axis exactly and
 * takes the second axis' length along its perpendicular (closest fit).
 */
import {
  applyToPoint,
  computeGroupFrame,
  computeItemLayout,
  groupContentMatrix,
  groupOriginShift,
  invert,
  multiply,
  type GroupFrame,
  type ItemExtent,
  type Mat2D,
} from '../core/geometry';
import { Align, type GroupItem, type Id, type ItemTransform, type SceneItem, type Vec2 } from '../core/model';

export type ContentSizeOf = (itemId: Id) => Vec2;
export type Measure = (item: SceneItem) => ItemExtent;

const ZERO: Vec2 = { x: 0, y: 0 };
const EPS = 1e-9;
const MATCH = 1e-6;

const finite = (v: number): number => (Number.isFinite(v) ? v : 0);

/**
 * Natural sizes by item. Group extents are derived from their children (the
 * same recursion the compositor performs); `contentSizeOf` is only consulted
 * for sources and nested scenes. Without it sources count as zero-sized
 * (children are located by their anchors) and nested scenes as the canvas.
 */
export function makeMeasure(contentSizeOf: ContentSizeOf | undefined, canvas: Vec2): Measure {
  const measure: Measure = (item) => {
    if (item.type === 'group') {
      const frame = computeGroupFrame(item.children, measure);
      return { size: frame.size, min: frame.min };
    }
    if (contentSizeOf) {
      const size = contentSizeOf(item.id);
      return { size: { x: Math.max(0, finite(size.x)), y: Math.max(0, finite(size.y)) } };
    }
    return { size: item.type === 'scene' ? canvas : ZERO };
  };
  return measure;
}

export function frameOf(group: GroupItem, measure: Measure): GroupFrame {
  return computeGroupFrame(group.children, measure);
}

/** Group-local pixels to the group's parent space. */
export function contentMatrixOf(group: GroupItem, measure: Measure): Mat2D {
  return groupContentMatrix(group.transform, frameOf(group, measure));
}

/** Group-local space of the innermost group to the scene root; `groups` run outermost first. */
export function chainMatrix(groups: readonly GroupItem[], measure: Measure): Mat2D {
  let m: Mat2D = { a: 1, b: 0, c: 0, d: 1, tx: 0, ty: 0 };
  for (const group of groups) m = multiply(m, contentMatrixOf(group, measure));
  return m;
}

/** Content pixels (after crop) of `transform` to its parent space, including a group's origin shift. */
function placement(transform: ItemTransform, extent: ItemExtent): Mat2D {
  const layout = computeItemLayout(transform, extent.size);
  const shift = extent.min ? groupOriginShift(transform, extent.min) : ZERO;
  return multiply({ a: 1, b: 0, c: 0, d: 1, tx: shift.x, ty: shift.y }, layout.matrix);
}

const near = (m: Mat2D, n: Mat2D): boolean =>
  Math.abs(m.a - n.a) < MATCH &&
  Math.abs(m.b - n.b) < MATCH &&
  Math.abs(m.c - n.c) < MATCH &&
  Math.abs(m.d - n.d) < MATCH &&
  Math.abs(m.tx - n.tx) < MATCH &&
  Math.abs(m.ty - n.ty) < MATCH;

const tidy = (v: number): number => {
  const r = Math.round(v * 1e9) / 1e9;
  return r === 0 ? 0 : r;
};

const wrap = (deg: number): number => tidy(deg % 360);

function tidied(t: ItemTransform): ItemTransform {
  return {
    ...t,
    position: { x: tidy(t.position.x), y: tidy(t.position.y) },
    scale: { x: tidy(t.scale.x), y: tidy(t.scale.y) },
    rotation: wrap(t.rotation),
    bounds: { x: tidy(t.bounds.x), y: tidy(t.bounds.y) },
  };
}

/**
 * Express `item.transform` (given in some space S1) in S2, where `change` maps
 * S1 points to S2. The on-screen result is identical. Throws when `change` is
 * singular (a zero-scale group).
 */
export function reexpress(item: { transform: ItemTransform }, extent: ItemExtent, change: Mat2D): void {
  const t = item.transform;
  if (!invert(change)) throw new Error('A group in this move has zero scale, so the item cannot be placed in it.');
  const target = multiply(change, placement(t, extent));
  const kx = Math.hypot(change.a, change.b);
  const ky = Math.hypot(change.c, change.d);
  const turn = (Math.atan2(change.b, change.a) * 180) / Math.PI;
  const det = change.a * change.d - change.b * change.c;
  const bounded = t.boundsType !== 'none';

  const candidates: ItemTransform[] = [
    // Rotation and scale of the space folded into the item (a flip rides on the y scale).
    {
      ...t,
      position: applyToPoint(change, t.position),
      rotation: t.rotation + turn,
      scale: { x: t.scale.x * kx, y: t.scale.y * ky * (det < 0 ? -1 : 1) },
      bounds: bounded ? { x: t.bounds.x * kx, y: t.bounds.y * ky } : t.bounds,
    },
    // Axis-aligned scaling or mirroring of the space: the item's rotation stays as it is.
    {
      ...t,
      position: applyToPoint(change, t.position),
      scale: { x: t.scale.x * change.a, y: t.scale.y * change.d },
      bounds: bounded ? { x: Math.abs(t.bounds.x * change.a), y: Math.abs(t.bounds.y * change.d) } : t.bounds,
    },
  ];
  for (const candidate of candidates) {
    const tidyCandidate = tidied(candidate);
    if (near(placement(tidyCandidate, extent), target)) {
      item.transform = tidyCandidate;
      return;
    }
  }

  const sx = Math.hypot(target.a, target.b);
  if (sx < EPS) throw new Error('The item would collapse to nothing in that space.');
  const theta = Math.atan2(target.b, target.a);
  const sy = -Math.sin(theta) * target.c + Math.cos(theta) * target.d;
  const baked: ItemTransform = {
    ...t,
    position: { x: 0, y: 0 },
    scale: { x: sx, y: sy },
    rotation: (theta * 180) / Math.PI,
    alignment: Align.TOP | Align.LEFT,
    boundsType: 'none',
    boundsAlignment: Align.CENTER,
    bounds: { x: 0, y: 0 },
  };
  const shift = extent.min ? groupOriginShift(baked, extent.min) : ZERO;
  baked.position = { x: target.tx - shift.x, y: target.ty - shift.y };
  item.transform = tidied(baked);
}

/**
 * A group's layout can depend on its children (centre or bounds aligned
 * groups). After a change of membership, put each affected group back so that
 * its content matrix, hence everything inside it, stays where it was.
 */
export function snapshotGroups(groups: readonly GroupItem[], measure: Measure): Map<Id, Mat2D> {
  return new Map(groups.map((g) => [g.id, contentMatrixOf(g, measure)]));
}

/** `groups` must be ordered innermost first. Groups whose linear part changed (bounds re-fit) are left alone. */
export function restoreGroups(groups: readonly GroupItem[], before: ReadonlyMap<Id, Mat2D>, measure: Measure): void {
  for (const group of groups) {
    const was = before.get(group.id);
    if (!was) continue;
    const now = contentMatrixOf(group, measure);
    const sameLinear =
      Math.abs(was.a - now.a) < MATCH &&
      Math.abs(was.b - now.b) < MATCH &&
      Math.abs(was.c - now.c) < MATCH &&
      Math.abs(was.d - now.d) < MATCH;
    if (!sameLinear) continue;
    const dx = was.tx - now.tx;
    const dy = was.ty - now.ty;
    if (Math.abs(dx) < MATCH && Math.abs(dy) < MATCH) continue;
    const p = group.transform.position;
    group.transform.position = { x: tidy(p.x + dx), y: tidy(p.y + dy) };
  }
}

/** On-screen centre of an item's drawn box in its parent space (the group origin shift included). */
export function boxCentre(transform: ItemTransform, extent: ItemExtent): Vec2 {
  const [tl, , br] = computeItemLayout(transform, extent.size).corners;
  const shift = extent.min ? groupOriginShift(transform, extent.min) : ZERO;
  return { x: (tl.x + br.x) / 2 + shift.x, y: (tl.y + br.y) / 2 + shift.y };
}
